import axios from "axios";
import queryString from "query-string";
import { store } from "../index";
import { triggerSaveSuccessNotif, triggerErrorNotif } from "../slices/notifications/thunk";
import utils from "../Common/utils";

const isDev = process.env.NODE_ENV === "development";

axios.defaults.baseURL = isDev ? process.env.REACT_APP_BACKEND_URL : "";
axios.defaults.headers.post["Content-Type"] = "application/json";

// Attach token to all requests
axios.interceptors.request.use(
  (config) => {
    const authUser = localStorage.getItem("authUser");
    const token = authUser ? JSON.parse(authUser).access : null;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    } else {
      delete config.headers["Authorization"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ======= AUTO REFRESH TOKEN LOGIC ON 401 =======
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// helper (place near the top of the file)
const hardLogout = () => {
  try {
    localStorage.removeItem("authUser");
    delete axios.defaults.headers.common["Authorization"];
    store.dispatch(triggerErrorNotif("Seja je potekla. Prosim prijavite se znova."));
    // If you have an auth slice action, you can also do:
    // store.dispatch(logoutUserSuccess(true));
    window.location.assign("/login"); // or your login route
  } catch {}
};

axios.interceptors.response.use(
  (response) => {
    const meta = response?.config?.meta;
    if (meta) {
      // Dispatch a global success toast when caller provided options
      store.dispatch(triggerSaveSuccessNotif(meta));
    }
    return response.data ?? response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const data = error.response?.data;
    const user = getLoggedinUser();

    // Parse DRF SimpleJWT style error
    const code = data?.code;
    const tokenType = data?.messages?.[0]?.token_type; // "access" | "refresh"
    const isTokenNotValid = status === 401 && code === "token_not_valid";
    const isRefreshCall = originalRequest?.url?.includes("/api/auth/token_refresh/");

    // ✅ If refresh call itself fails OR backend says refresh token is bad → hard logout
    if (isRefreshCall || (isTokenNotValid && tokenType === "refresh")) {
      hardLogout();
      return Promise.reject(error);
    }

    // ✅ If access token is expired AND we haven't retried yet → try one refresh
    if (isTokenNotValid && tokenType === "access" && !originalRequest._retry) {
      if (!user?.refresh) {
        hardLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await postRefreshToken({ refresh: user.refresh });
        const { access: newAccess, expiery } = res; // <-- bug fix here

        const updatedUser = {
          ...user,
          access: newAccess,
          expiery,
          // Keep org config fresh on silent refresh -- see thunk.js refreshJWT.
          ...(res.organization ? { organization: res.organization } : {}),
          ...(res.profile ? { profile: res.profile } : {}),
        };
        localStorage.setItem("authUser", JSON.stringify(updatedUser));

        axios.defaults.headers.common["Authorization"] = `Bearer ${newAccess}`;
        originalRequest.headers["Authorization"] = `Bearer ${newAccess}`;

        processQueue(null, newAccess);
        return axios(originalRequest);
      } catch (err) {
        processQueue(err, null);
        hardLogout(); // <-- logout if refresh fails for any reason
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }


    // Other errors → your existing notifier
    let message = "Prišlo je do nepričakovane napake";
    if (status === 400) {
      if (typeof data === "string") message = `Napaka pri validaciji | ${data}`;
      else if (Array.isArray(data)) message = `Napaka pri validaciji | ${data.join("\n")}`
      else {
        // Surface DRF field errors ({"title": ["..."]}) instead of a bare
        // "validation error" the user cannot act on.
        const details = Object.values(data || {})
          .map((val) => (Array.isArray(val) ? val.join(" ") : typeof val === "string" ? val : ""))
          .filter(Boolean)
          .join("\n");
        message = details ? `Napaka pri validaciji | ${details}` : `Napaka pri validaciji`
      }
    } else {
      switch (status) {
        case 403:
          message = data?.detail || "Prepovedano";
          store.dispatch(triggerErrorNotif(message));
          return Promise.resolve(null); // swallow 403 errors
        case 404:
          message = "Ni najdeno";
          break;
        case 500:
          message = "Interna napaka strežnika";
          break;
        default:
          message = data?.detail || error.message || message;
          console.log("MESSAGE", message)
      }
    }
    store.dispatch(triggerErrorNotif(message));
    return Promise.reject(error);
  }
);

// Auth header helper
const setAuthorization = (token) => {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

// ✅ Exported helper for getting current user
const getLoggedinUser = () => {
  const user = localStorage.getItem("authUser");
  return user ? JSON.parse(user) : null;
};

// ✅ Refresh token API call
const postRefreshToken = (data) => {
  return axios.post("/api/auth/token_refresh/", data, {successMsg: null});
};

// Reusable API wrapper class
class APIClient {
  get = (url, params) => {
    return axios.get(`${url}?${queryString.stringify(utils.cleanEmpty(params))}`);
  };

  create = (url, data, options = {}) => {
    const { successMsg, ...config } = options;
    return axios.post(url, data, { ...config, meta: { successMsg } });
  };

  update = (url, data, options = {}) => {
    const { successMsg, ...config } = options;
    return axios.patch(url, data, { ...config, meta: { successMsg } });
  };

  put = (url, data, options = {}) => {
    const { successMsg, ...config } = options;
    return axios.put(url, data, { ...config, meta: { successMsg } });
  };

  post = (url, data, options = {}) => {
    const { successMsg, ...config } = options;
    return axios.post(url, data, { ...config, meta: { successMsg } });
  };

  delete = (url, data, options = {}) => {
    const { successMsg, ...config } = options;
    return axios.delete(url, { data, ...config, meta: { successMsg } });
  };
}

export { APIClient, setAuthorization, getLoggedinUser, postRefreshToken };
